import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import { useProject } from '../../context/ProjectContext';
import api from '../../services/api';
import { VISIT_TYPES } from '../../utils/constants';
import { formatOffsetForDisplay, formatDateTimeDDMMMYYYY, formatDateDDMMMYYYY } from '../../utils/dateFormat';
import { exportActionItemReportToExcel } from '../../utils/exportToExcel';
import Alert from '../Common/Alert';
import Spinner from '../Common/Spinner';
import './ReportsHome.css';
import './AuditReport.css';

const ACTION_ITEM_REPORT_COLUMNS = [
  { key: 'project', label: 'Project' },
  { key: 'siteId', label: 'Site ID' },
  { key: 'siteName', label: 'Site Name' },
  { key: 'visitType', label: 'Visit Type' },
  { key: 'referenceNo', label: 'Reference No' },
  { key: 'fieldName', label: 'Field Name' },
  { key: 'actualEndDate', label: 'Actual End Date' },
  { key: 'dateOfIssueCreation', label: 'Date of Issue Creation' },
  { key: 'issueDescription', label: 'Issue Description' },
  { key: 'issueAssignedTo', label: 'Issue Assigned To' },
  { key: 'expectedDateOfClosure', label: 'Expected Date of Closure' },
  { key: 'oldDescriptionOfAction', label: 'Old Description of Action' },
  { key: 'newDescriptionOfAction', label: 'New Description of Action' },
  { key: 'currentStatus', label: 'Current Status' },
  { key: 'closeIssueDate', label: 'Close Issue date' },
  { key: 'closureRemarks', label: 'Closure remarks' },
  { key: 'performedBy', label: 'Performed By' },
  { key: 'performedOnUtc', label: 'Performed On UTC' },
  { key: 'performedAtOffset', label: 'Performed At Offset' },
];

const ACTION_ITEM_REPORT_EXPORT_COLUMNS = ACTION_ITEM_REPORT_COLUMNS;

const ACTION_ITEM_EXPORT_COL_WIDTHS = [18, 14, 28, 24, 16, 26, 16, 20, 36, 24, 24, 30, 30, 16, 20, 28, 30, 20, 18];

/** Date-only for export: 16-Mar-2026 (empty when missing). */
function formatDateOnly(value) {
  if (value == null) return '';
  const s = formatDateDDMMMYYYY(value);
  return s === '—' ? '' : s;
}

/** Performed on UTC column / audit instant: 30-Apr-2026 03:32 (24h, UTC components). */
function formatPerformedOnUtc(value) {
  if (value == null) return '';
  const s = formatDateTimeDDMMMYYYY(value, true);
  return s === '—' ? '' : s;
}

/** Excel Visit Type: full name (e.g. SSV → Site Selection Visit); pass through if already full text. */
function formatVisitTypeForExport(value) {
  if (value == null) return '';
  const v = String(value).trim();
  if (!v) return '';
  const code = v.toUpperCase();
  if (VISIT_TYPES[code]) return VISIT_TYPES[code];
  return v;
}

const ActionItemReport = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { vRecordedSign, vRecordedAtOffset } = useSession();
  const { currentProject: contextProject } = useProject();
  const projectCode = contextProject && contextProject !== 'all' ? (typeof contextProject === 'string' ? contextProject : contextProject?.projectCode ?? contextProject?.ProjectCode) : '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    const params = {};
    if (projectCode) params.projectCode = projectCode;
    api
      .get('/Report/action-items', { params })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setRows(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err?.message || 'Failed to load issues.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectCode]);

  const handleExportExcel = () => {
    setError(null);
    setExportLoading(true);
    const exportKeys = ACTION_ITEM_REPORT_EXPORT_COLUMNS.map((c) => c.key);
    const exportRows = rows.map((r) => {
      const row = {};
      exportKeys.forEach((k) => {
        if (k === 'project') {
          row[k] = (r.project ?? r.Project ?? '').toString().trim();
        } else if (k === 'siteId') {
          row[k] = (r.siteId ?? r.SiteId ?? '').toString().trim();
        } else if (k === 'siteName') {
          row[k] = (r.siteName ?? r.SiteName ?? '').toString().trim();
        } else if (k === 'visitType') {
          row[k] = formatVisitTypeForExport(r.visitType ?? r.VisitType);
        } else if (k === 'referenceNo') {
          row[k] = (r.referenceNo ?? r.ReferenceNo ?? '').toString().trim();
        } else if (k === 'fieldName') {
          row[k] = (r.fieldName ?? r.FieldName ?? '').toString().trim();
        } else if (k === 'actualEndDate') {
          row[k] = formatDateOnly(r.actualEndDate ?? r.ActualEndDate);
        } else if (k === 'dateOfIssueCreation') {
          row[k] = formatDateOnly(r.dateOfIssueCreation ?? r.DateOfIssueCreation);
        } else if (k === 'issueDescription') {
          row[k] = (r.issueDescription ?? r.IssueDescription ?? '').toString().trim();
        } else if (k === 'issueAssignedTo') {
          row[k] = (r.issueAssignedTo ?? r.IssueAssignedTo ?? '').toString().trim();
        } else if (k === 'expectedDateOfClosure') {
          row[k] = formatDateOnly(r.expectedDateOfClosure ?? r.ExpectedDateOfClosure);
        } else if (k === 'oldDescriptionOfAction') {
          row[k] = (r.oldDescriptionOfAction ?? r.OldDescriptionOfAction ?? '').toString().trim();
        } else if (k === 'newDescriptionOfAction') {
          row[k] = (r.newDescriptionOfAction ?? r.NewDescriptionOfAction ?? '').toString().trim();
        } else if (k === 'currentStatus') {
          row[k] = (r.currentStatus ?? r.CurrentStatus ?? '').toString().trim();
        } else if (k === 'closeIssueDate') {
          row[k] = formatDateOnly(r.closeIssueDate ?? r.CloseIssueDate);
        } else if (k === 'closureRemarks') {
          row[k] = (r.closureRemarks ?? r.ClosureRemarks ?? '').toString().trim();
        } else if (k === 'performedBy') {
          row[k] = (r.performedBy ?? r.PerformedBy ?? '').toString().trim();
        } else if (k === 'performedOnUtc') {
          row[k] = formatPerformedOnUtc(r.performedOnUtc ?? r.PerformedOnUtc);
        } else if (k === 'performedAtOffset') {
          const raw = r.performedAtOffset ?? r.PerformedAtOffset ?? '';
          row[k] = raw !== '' && formatOffsetForDisplay(raw) !== '—' ? formatOffsetForDisplay(raw) : '';
        } else {
          row[k] = r[k] ?? '';
        }
      });
      return row;
    });
    try {
      const fileName = `Issue_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const firstName = (user?.firstName ?? user?.FirstName ?? user?.vFirstName ?? '').trim();
      const lastName = (user?.lastName ?? user?.LastName ?? user?.vLastName ?? '').trim();
      const userName = (user?.username ?? user?.Username ?? user?.vLoginName ?? user?.vUserName ?? '').trim();
      const profile = (user?.role ?? user?.Role ?? user?.profileCode ?? user?.vUserTypeCode ?? '').trim();
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      const exportedBy = fullName ? `${fullName} (${userName || '—'})(${profile || '—'})` : (vRecordedSign && vRecordedSign.trim() !== '' ? vRecordedSign.trim() : '');
      const offset = (vRecordedAtOffset && String(vRecordedAtOffset).trim() !== '') ? String(vRecordedAtOffset).trim() : '+05:30';
      exportActionItemReportToExcel(exportRows, ACTION_ITEM_REPORT_EXPORT_COLUMNS, fileName, {
        formName: 'Issue Report',
        exportedBy,
        exportedOnUtc: formatDateTimeDDMMMYYYY(new Date(), true),
        offset,
        readOnlyAllowResize: true,
        colWidths: ACTION_ITEM_EXPORT_COL_WIDTHS,
      });
    } catch (err) {
      setError(err?.message || 'Export failed.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="audit-report">
      <div className="audit-report-header" style={{ marginBottom: '16px' }}>
        <h2>Issue Report</h2>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="audit-report-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleExportExcel}
          disabled={exportLoading || loading || rows.length === 0}
          title={exportLoading ? 'Exporting…' : 'Export to Excel'}
        >
          {exportLoading ? 'Exporting…' : 'Export'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/reports')}>
          Close
        </button>
      </div>      
    </div>
  );
};

export default ActionItemReport;
